import {
  ChevronDown,
  ChevronUp,
  Heart,
  HelpCircle,
  Lightbulb,
  PartyPopper,
  Smile,
  Star,
  ThumbsUp,
  UserPlus,
} from 'lucide-react-native';
import type { ComponentType, ReactNode } from 'react';
import { Image, Platform, Pressable, StyleSheet, View } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useHoverEffect } from '../../../hooks/useHoverEffect';
import { colors } from '../../../theme/colors';
import { listDivider, listRow } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import { resolveCommenterProfileUrl } from '../utils/commenterEnrichment';

/**
 * Shared engagement chrome for social posts: who reacted, who commented, and
 * the control that opens either list.
 *
 * Two rules hold this together. Reaction types are drawn glyphs from the icon
 * library, never emoji — emoji render differently on every platform and read as
 * clip art next to a Lucide set. And every reaction glyph shares one tint: the
 * shape already names the reaction, so hue would only turn the row into a
 * rainbow and dilute the one colour that means "interactive".
 */

type ReactionIcon = ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;

const REACTION_ICONS: Record<string, ReactionIcon> = {
  LIKE: ThumbsUp,
  APPRECIATION: Star,
  EMPATHY: Heart,
  PRAISE: PartyPopper,
  INTEREST: Lightbulb,
  ENTERTAINMENT: Smile,
  CURIOUS: HelpCircle,
  MAYBE: HelpCircle,
};

export function ReactionGlyph({
  type,
  size = 12,
  color = colors.text.secondary,
}: Readonly<{ type: string; size?: number; color?: string }>) {
  const Icon = REACTION_ICONS[type?.toUpperCase()] ?? ThumbsUp;
  return <Icon size={size} color={color} strokeWidth={2} />;
}

/** A reaction type and how many people picked it. */
export function ReactionPill({ type, count }: Readonly<{ type: string; count: number }>) {
  return (
    <HStack spacing="xs" align="center" style={styles.pill}>
      <ReactionGlyph type={type} />
      <Text color={colors.text.secondary} style={styles.pillCount}>
        {count}
      </Text>
    </HStack>
  );
}

/**
 * Turn a commenter or reactor into a lead. One control, one place — the two
 * post surfaces each had their own version and they had already drifted.
 */
export function EnrichButton({
  profileUrl,
  name,
  onEnrich,
}: Readonly<{
  profileUrl?: string | null;
  name: string;
  onEnrich: (profileUrl: string, name: string) => void;
}>) {
  const { t } = useTranslation();
  const safeProfileUrl = resolveCommenterProfileUrl(profileUrl ?? undefined);
  if (!safeProfileUrl) return null;

  return (
    <Pressable
      testID="lead_activity_commenter_enrich_button"
      onPress={() => onEnrich(safeProfileUrl, name)}
      accessibilityRole="button"
      accessibilityLabel={`${t('leads.detail.linkedinPosts.enrichCommenter')} ${name}`.trim()}
      hitSlop={8}
      style={styles.enrich}
    >
      <UserPlus size={11} color={colors.primary[400]} strokeWidth={2} />
    </Pressable>
  );
}

const FACE = 22;

interface Face {
  name: string;
  imageUrl?: string | null;
}

/**
 * Overlapping avatars that show who is behind a count before it is expanded.
 * Each face carries a ring in the card fill so the stack stays readable; that
 * ring is a graphic separator on an avatar, not a container border.
 */
export function Facepile({ faces, max = 3 }: Readonly<{ faces: Face[]; max?: number }>) {
  const shown = faces.slice(0, max);
  if (shown.length === 0) return null;

  return (
    <View style={styles.facepile}>
      {shown.map((face, index) => (
        <View key={`${face.name}-${index}`} style={[styles.face, index > 0 && styles.faceOverlap]}>
          {face.imageUrl ? (
            <Image
              source={{ uri: face.imageUrl }}
              style={styles.faceImage}
              accessibilityLabel={face.name}
            />
          ) : (
            <Text color={colors.text.secondary} style={styles.faceInitials}>
              {initialsOf(face.name)}
            </Text>
          )}
        </View>
      ))}
    </View>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0][0]}${parts.at(-1)?.[0] ?? ''}`.toUpperCase();
}

/**
 * The control that opens a comment or reaction list.
 *
 * It used to be a teal text link. On a card where teal already means "primary
 * action", a teal link for "show me ten comments" spends the loudest colour in
 * the system on the quietest job. This is a row: faces, a plain-language count,
 * a chevron, and a hover fill that says it responds.
 */
export function EngagementDisclosure({
  label,
  faces,
  expanded,
  onToggle,
  leading,
  testID,
}: Readonly<{
  label: string;
  faces?: Face[];
  expanded: boolean;
  onToggle: () => void;
  leading?: ReactNode;
  testID?: string;
}>) {
  const { isHovered, hoverProps } = useHoverEffect();
  const Chevron = expanded ? ChevronUp : ChevronDown;

  return (
    <Pressable
      {...hoverProps}
      onPress={onToggle}
      accessibilityRole="button"
      accessibilityState={{ expanded }}
      accessibilityLabel={label}
      testID={testID}
      style={[styles.disclosure, listRow({ hovered: isHovered })]}
    >
      <HStack spacing="sm" align="center">
        {leading}
        {faces && faces.length > 0 && <Facepile faces={faces} />}
        <Text
          variant="caption"
          color={colors.text.secondary}
          numberOfLines={1}
          style={styles.disclosureLabel}
        >
          {label}
        </Text>
        <Box flex={1} />
        <Chevron size={14} color={colors.text.tertiary} strokeWidth={2} />
      </HStack>
    </Pressable>
  );
}

/**
 * The expanded body of a disclosure. Indented to the disclosure's text, with no
 * left rule — a coloured or hairline edge stripe is a retired pattern, and the
 * indent already says "this belongs to the row above".
 */
export function EngagementPanel({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <VStack spacing="lg" style={styles.panel}>
      {children}
    </VStack>
  );
}

/** The hairline that separates one post from the next inside a card. */
export function PostDivider() {
  return <View style={listDivider} />;
}

const styles = StyleSheet.create({
  enrich: {
    backgroundColor: colors.primary.muted,
    borderRadius: tokens.radius.full,
    padding: 3,
  },
  pill: {
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    borderRadius: tokens.radius.full,
    backgroundColor: colors.surface.row,
  },
  pillCount: {
    fontFamily: darkPrecisionFontFamilies.metric,
    fontSize: tokens.fontSize.xs,
  },
  facepile: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  face: {
    width: FACE,
    height: FACE,
    borderRadius: FACE / 2,
    backgroundColor: colors.surface.iconWell,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: colors.background.card,
  },
  faceOverlap: {
    marginLeft: -8,
  },
  faceImage: {
    width: '100%',
    height: '100%',
  },
  faceInitials: {
    fontFamily: darkPrecisionFontFamilies.uiMedium,
    fontSize: 9,
    lineHeight: 11,
  },
  disclosure: {
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.sm,
    marginHorizontal: -tokens.spacing.sm,
    minHeight: 44,
    justifyContent: 'center',
    ...Platform.select({
      web: {
        transitionProperty: 'background-color',
        transitionDuration: `${tokens.duration.fast}ms`,
        transitionTimingFunction: tokens.easing.default,
      } as object,
      default: {},
    }),
  },
  disclosureLabel: {
    fontWeight: '500',
  },
  panel: {
    paddingLeft: tokens.spacing.sm,
    paddingTop: tokens.spacing.sm,
  },
});
